import type { Identifier } from "ra-core";

import {
  isActiveAgreement,
  isMaintenanceStale,
  renewalState,
  RENEWAL_SOON_DAYS,
} from "../../agreements/agreements";
import { formatKr } from "../../deals/DealLinesEditor";
import { isDealOpen } from "../../deals/dealStatus";
import {
  NEXT_STEP_TYPES,
  groupTasksByDeal,
  isDealRotting,
  offerExpiresSoon,
  pickNextStep,
  silentDays,
} from "../../deals/nextStep";
import { addDaysKey, osloDateKey } from "../../misc/osloDate";
import { daysBetween, workdaysBetween } from "../../misc/workdays";
import type {
  Company,
  CompanyNextStep,
  CompanyNote,
  CompanyProduct,
  Deal,
  DealStage,
  MiminNotification,
  Product,
  WorkItem,
} from "../../types";
import {
  CLEANUP_DAYS,
  QUIET_DAYS,
  type FocusRow,
  type Signal,
} from "./ranking";

// Gjør Mimins data om til rader for «Gjør nå» (spesifikasjonen kap. 4.1).
// Én rad er én ting å gjøre: en oppgave, en mulighet, en lead, en kunde, en
// avtale eller funn på nettstedet. En oppgave som er neste steg på en
// mulighet, står én gang, som neste steg, med mulighetens signaler lagt til.

export type LastContactRow = {
  company_id: Identifier;
  last_contact_at: string | null;
};

export type DriftScan = {
  companyId: Identifier;
  findings: string[];
  scannedAt: string;
};

export type BuildInput = {
  today: string;
  tasks: WorkItem[];
  customerSteps: CompanyNextStep[];
  followUpNotes: CompanyNote[];
  openDeals: Deal[];
  renewalDeals: Deal[];
  lead: {
    unowned: Deal[];
    noContact: Deal[];
    parseFailed: Deal[];
  };
  companies: Company[];
  lastContacts: LastContactRow[];
  agreements: CompanyProduct[];
  products: Product[];
  drift: DriftScan[];
  notifications: MiminNotification[];
  dealStages: DealStage[];
  /** Avtaler er bare for administrator. */
  isAdmin: boolean;
};

const dateKey = (value: string | null | undefined) =>
  value ? (value.length > 10 ? osloDateKey(new Date(value)) : value) : null;

const plainText = (html?: string | null) => {
  if (!html) return null;
  const text = html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text ? (text.length > 60 ? `${text.slice(0, 60)}…` : text) : null;
};

const isDealStepTask = (t: WorkItem) =>
  t.deal_id != null &&
  (t.task_type == null || NEXT_STEP_TYPES.includes(t.task_type));

// Frist som signal: forfalt (med dager) eller i dag. Senere frister er ikke
// noe å gjøre nå.
const dueSignal = (
  due: string | null | undefined,
  today: string,
  overdueKind: "task_overdue" | "next_step_overdue" = "task_overdue",
): Signal | null => {
  if (!due) return null;
  if (due < today) return { kind: overdueKind, days: daysBetween(due, today) };
  if (due === today) return { kind: "task_today" };
  return null;
};

const INTERVAL_SUFFIX: Record<string, string> = {
  monthly: "/mnd",
  quarterly: "/kvartal",
  semiannual: "/halvår",
  yearly: "/år",
};

const monthsSince = (from: string, today: string) => {
  const [fy, fm, fd] = from.split("-").map(Number);
  const [ty, tm, td] = today.split("-").map(Number);
  return Math.max(0, (ty - fy) * 12 + (tm - fm) - (td < fd ? 1 : 0));
};

/** Første funn og «og n til». */
export const findingsSummary = (findings: string[]) => {
  if (findings.length <= 2) return findings.join(" og ");
  return `${findings.slice(0, 2).join(", ")} og ${findings.length - 2} til`;
};

/** Funn på kunden som allerede har en åpen oppgave. */
export const openDriftFindings = (tasks: WorkItem[], companyId: Identifier) =>
  new Set(
    tasks
      .filter(
        (t) =>
          !t.completed_at &&
          !!t.drift_finding &&
          String(t.company_id) === String(companyId),
      )
      .map((t) => t.drift_finding as string),
  );

export const buildRows = (input: BuildInput): FocusRow[] => {
  const { today, dealStages } = input;
  const companyById = new Map(input.companies.map((c) => [String(c.id), c]));
  const companyName = (id: Identifier | null | undefined) =>
    id == null ? null : (companyById.get(String(id))?.name ?? null);
  const stageLabel = (value: string | null | undefined) =>
    dealStages.find((s) => s.value === value)?.label ?? value ?? null;
  const inbox = new Set(
    dealStages.filter((s) => s.is_inbox).map((s) => s.value),
  );
  const rows: FocusRow[] = [];

  // Varsler til deg: alltid dine, alltid øverst blant det som haster.
  for (const n of input.notifications) {
    rows.push({
      key: `notification:${n.id}`,
      type: "notification",
      title: n.text,
      signals: [{ kind: "notification" }],
      score: 0,
      ownerId: n.recipient_id,
      alwaysMine: true,
      triggerDate: dateKey(n.created_at),
      meta: ["Varsel"],
      path:
        n.kind === "error_log"
          ? "/settings#feillogg"
          : n.deal_id != null
            ? `/deals/${n.deal_id}/show`
            : n.project_id != null
              ? `/projects/${n.project_id}/show`
              : "/",
      cleanup: false,
      source: n,
    });
  }

  // Muligheter og leads.
  const openDeals = input.openDeals.filter(
    (d) => !d.archived_at && isDealOpen(d),
  );
  const openDealIds = new Set(openDeals.map((d) => String(d.id)));
  const tasksByDeal = groupTasksByDeal(input.tasks.filter(isDealStepTask));
  const leadFlags = new Map<string, Signal[]>();
  const flag = (deal: Deal, signal: Signal) => {
    const list = leadFlags.get(String(deal.id)) ?? [];
    if (!list.some((s) => s.kind === signal.kind)) list.push(signal);
    leadFlags.set(String(deal.id), list);
  };
  // Leads som har ventet for lenge, vises for alle (useLeadAttention).
  const sharedLeads = new Set<string>();
  const leadDeals = new Map<string, Deal>();
  for (const d of input.lead.unowned) {
    flag(d, { kind: "lead_unowned" });
    sharedLeads.add(String(d.id));
    leadDeals.set(String(d.id), d);
  }
  for (const d of input.lead.noContact) {
    flag(d, { kind: "lead_no_contact" });
    sharedLeads.add(String(d.id));
    leadDeals.set(String(d.id), d);
  }
  for (const d of input.lead.parseFailed) {
    flag(d, { kind: "lead_parse_failed" });
    sharedLeads.add(String(d.id));
    leadDeals.set(String(d.id), d);
  }
  const allDeals = [
    ...openDeals,
    ...[...leadDeals.values()].filter((d) => !openDealIds.has(String(d.id))),
  ];

  for (const deal of allDeals) {
    const isLead = !!deal.stage && inbox.has(deal.stage);
    const stage = stageLabel(deal.stage);
    const next = pickNextStep(tasksByDeal.get(String(deal.id)));
    const signals: Signal[] = [...(leadFlags.get(String(deal.id)) ?? [])];
    const createdKey = dateKey(deal.created_at) ?? today;
    if (isLead && deal.sales_id == null) {
      const lead = signals.find((s) => s.kind === "lead_unowned");
      const days = Math.max(0, daysBetween(createdKey, today));
      if (lead) lead.days = days;
      else signals.push({ kind: "lead_unowned", days });
    }
    let overdueStep = false;
    if (next?.due_date && next.due_date < today) {
      overdueStep = true;
      signals.push({
        kind: "next_step_overdue",
        days: daysBetween(next.due_date, today),
      });
    } else if (!next && !isLead) {
      signals.push({ kind: "deal_no_step", stage });
    }
    const stageRule = dealStages.find((s) => s.value === deal.stage);
    const rotting = isDealRotting(deal, dealStages, today);
    if (rotting) {
      signals.push({
        kind: "deal_stalled",
        days: silentDays(deal, today),
        stage,
      });
    }
    if (offerExpiresSoon(deal, today)) {
      signals.push({ kind: "offer_expiring", date: deal.offer_valid_until });
    }
    if (!signals.length) continue;

    const stageSince = dateKey(deal.stage_changed_at) ?? createdKey;
    const daysInStage = Math.max(0, daysBetween(stageSince, today));
    const activity = dateKey(deal.last_activity_at) ?? createdKey;
    const limit = stageRule?.rot_after_workdays ?? 0;
    const cleanup =
      (overdueStep && daysBetween(next!.due_date!, today) > CLEANUP_DAYS) ||
      // Stille i over dobbelt så lenge som stadiets grense, og over 30 dager,
      // så «har ligget i over 30 dager» stemmer for alt i Rydd opp.
      (rotting &&
        limit > 0 &&
        workdaysBetween(activity, today) > 2 * limit &&
        silentDays(deal, today) > CLEANUP_DAYS);
    const oneTime = deal.one_time_value ?? 0;
    const monthly = deal.monthly_value ?? 0;
    const monthlyOnly = !oneTime && monthly > 0;
    const value = monthlyOnly
      ? monthly
      : (deal.annual_value ?? oneTime + 12 * monthly) || deal.amount || null;
    const company = companyName(deal.company_id);
    rows.push({
      key: `deal:${deal.id}`,
      type: isLead ? "lead" : overdueStep ? "next_step" : "opportunity",
      title:
        overdueStep && next?.title ? `${deal.name} – ${next.title}` : deal.name,
      signals,
      score: 0,
      ownerId: deal.sales_id ?? null,
      unowned: isLead && deal.sales_id == null,
      alwaysMine: sharedLeads.has(String(deal.id)),
      companyId: deal.company_id ?? null,
      companyName: company,
      triggerDate: overdueStep ? next!.due_date! : stageSince,
      value,
      monthlyOnly,
      markerDate: overdueStep ? next!.due_date : null,
      meta: isLead
        ? [stage ?? "Lead", company].filter((x): x is string => !!x)
        : [
            overdueStep ? "Neste steg" : "Mulighet",
            stage
              ? `${daysInStage} ${daysInStage === 1 ? "dag" : "dager"} i ${stage}`
              : null,
          ].filter((x): x is string => !!x),
      path: `/deals/${deal.id}/show`,
      cleanup,
      source: { deal, next },
    });
  }

  // Oppgaver (uten de som er neste steg på en åpen mulighet).
  for (const t of input.tasks) {
    if (t.completed_at) continue;
    if (isDealStepTask(t) && openDealIds.has(String(t.deal_id))) continue;
    const signal = dueSignal(t.due_date, today);
    if (!signal) continue;
    const company = companyName(t.company_id);
    rows.push({
      key: `task:${t.id}`,
      type: "task",
      title: t.title,
      signals: [signal],
      score: 0,
      ownerId: t.assignee_id ?? null,
      companyId: t.company_id ?? null,
      companyName: company,
      triggerDate: t.due_date ?? null,
      markerDate: t.due_date ?? null,
      meta: ["Oppgave", company ?? "Ingen kunde"],
      path: `/work_items/${t.id}`,
      cleanup: (signal.days ?? 0) > CLEANUP_DAYS,
      source: t,
    });
  }

  // Kundens neste steg (kundekortet).
  for (const s of input.customerSteps) {
    if (s.completed_at || !s.description) continue;
    const signal = dueSignal(s.due_date, today);
    if (!signal) continue;
    const company = companyName(s.company_id) ?? "";
    rows.push({
      key: `step:${s.id}`,
      type: "next_step",
      title: company ? `${company} – ${s.description}` : s.description,
      signals: [signal],
      score: 0,
      ownerId: s.owner_id ?? null,
      companyId: s.company_id,
      companyName: company,
      triggerDate: s.due_date ?? null,
      markerDate: s.due_date ?? null,
      meta: ["Neste steg", company].filter(Boolean),
      path: `/companies/${s.company_id}/show`,
      cleanup: (signal.days ?? 0) > CLEANUP_DAYS,
      source: s,
    });
  }

  // Oppfølging på kundenotat («ring tilbake fredag»).
  for (const n of input.followUpNotes) {
    const signal = dueSignal(n.follow_up_date, today);
    if (!signal) continue;
    const company = companyName(n.company_id) ?? "";
    rows.push({
      key: `note:${n.id}`,
      type: "task",
      title: `Ring tilbake: ${company}`,
      signals: [signal],
      score: 0,
      ownerId: n.sales_id ?? null,
      companyId: n.company_id,
      companyName: company,
      triggerDate: n.follow_up_date ?? null,
      markerDate: n.follow_up_date ?? null,
      meta: ["Oppfølging", plainText(n.content)].filter(
        (x): x is string => !!x,
      ),
      path: `/companies/${n.company_id}/show`,
      cleanup: (signal.days ?? 0) > CLEANUP_DAYS,
      source: n,
    });
  }

  // Fornyelser på muligheter (renewal_date).
  const renewalHorizon = addDaysKey(today, RENEWAL_SOON_DAYS);
  for (const d of input.renewalDeals) {
    if (d.archived_at || !d.renewal_date || d.renewal_date > renewalHorizon) {
      continue;
    }
    const company = companyName(d.company_id);
    rows.push({
      key: `renewal:${d.id}`,
      type: "agreement",
      title: d.name,
      signals: [{ kind: "agreement_renewal", date: d.renewal_date }],
      score: 0,
      ownerId: d.sales_id ?? null,
      companyId: d.company_id,
      companyName: company,
      triggerDate: d.renewal_date,
      markerDate: d.renewal_date,
      meta: ["Fornyelse", company].filter((x): x is string => !!x),
      path: `/deals/${d.id}/show`,
      cleanup: false,
      source: d,
    });
  }

  // Løpende avtaler (bare administrator). En avtale som aldri er
  // vedlikeholdt, tas ikke med: etter importen fra Tripletex gjelder det
  // nesten alle, og lista ville druknet. Avtaler viser dem fortsatt.
  if (input.isAdmin) {
    const productById = new Map(input.products.map((p) => [String(p.id), p]));
    for (const a of input.agreements) {
      if (!isActiveAgreement(a, today)) continue;
      const signals: Signal[] = [];
      if (
        a.last_maintained_on &&
        isMaintenanceStale(a.last_maintained_on, today)
      ) {
        signals.push({
          kind: "agreement_maintenance",
          count: monthsSince(a.last_maintained_on, today),
        });
      }
      const renewal = renewalState(a.renewal_date, today);
      if (renewal === "soon" || renewal === "overdue") {
        signals.push({ kind: "agreement_renewal", date: a.renewal_date });
      }
      if (!signals.length) continue;
      const company = companyById.get(String(a.company_id));
      const product = productById.get(String(a.product_id));
      const price = (a.price ?? product?.sales_price ?? 0) * (a.quantity ?? 1);
      const maintenance = signals[0].kind === "agreement_maintenance";
      rows.push({
        key: `agreement:${a.id}`,
        type: "agreement",
        title: [company?.name, product?.name].filter(Boolean).join(" – "),
        signals,
        score: 0,
        ownerId: company?.sales_id ?? null,
        companyId: a.company_id,
        companyName: company?.name ?? null,
        triggerDate: maintenance ? a.last_maintained_on! : a.renewal_date!,
        value: price,
        markerDate: maintenance ? a.last_maintained_on : a.renewal_date,
        meta: [
          "Avtale",
          maintenance ? "Trenger vedlikehold" : "Fornyes snart",
          price
            ? `${formatKr(price)} kr${INTERVAL_SUFFIX[a.billing_interval] ?? ""}`
            : null,
        ].filter((x): x is string => !!x),
        path: "/agreements",
        cleanup: false,
        source: a,
      });
    }
  }

  // Funn på nettstedet (Driftsradar).
  // Funn som allerede har en åpen oppgave («Lag oppgaver»), teller ikke.
  for (const scan of input.drift) {
    const company = companyById.get(String(scan.companyId));
    if (!company) continue;
    const open = openDriftFindings(input.tasks, company.id);
    const findings = scan.findings.filter((f) => !open.has(f));
    if (!findings.length) continue;
    rows.push({
      key: `drift:${company.id}`,
      type: "drift",
      title: company.name,
      signals: [{ kind: "drift", count: findings.length }],
      score: 0,
      ownerId: company.sales_id ?? null,
      companyId: company.id,
      companyName: company.name,
      triggerDate: dateKey(scan.scannedAt),
      meta: ["Driftsradar", findingsSummary(findings)],
      path: `/companies/${company.id}/show/drift`,
      cleanup: false,
      source: scan,
    });
  }

  // Kunder uten kontakt. Signalet legges på kundens avtale- eller
  // driftsrad når den finnes; ellers får kunden en egen rad.
  for (const lc of input.lastContacts) {
    const company = companyById.get(String(lc.company_id));
    if (!company || company.archived_at || company.is_own_company) continue;
    const lastKey = dateKey(lc.last_contact_at);
    const days = lastKey ? daysBetween(lastKey, today) : null;
    if (days != null && days < QUIET_DAYS) continue;
    const signal: Signal =
      days == null
        ? { kind: "customer_never" }
        : { kind: "customer_quiet", days };
    const host = rows.find(
      (r) =>
        (r.type === "agreement" || r.type === "drift") &&
        r.companyId != null &&
        String(r.companyId) === String(company.id),
    );
    if (host) {
      host.signals.push(signal);
      continue;
    }
    rows.push({
      key: `company:${company.id}`,
      type: "customer",
      title: company.name,
      signals: [signal],
      score: 0,
      ownerId: company.sales_id ?? null,
      unowned: company.sales_id == null,
      companyId: company.id,
      companyName: company.name,
      triggerDate: lastKey ?? dateKey(company.created_at),
      meta: ["Kunde"],
      path: `/companies/${company.id}/show`,
      cleanup: false,
      source: company,
    });
  }

  return rows;
};
